import 'overlayscrollbars/overlayscrollbars.css';
import {
  css, type Interpolation,
} from '@emotion/react';
import type { EventListeners } from 'overlayscrollbars';
import {
  OverlayScrollbarsComponent, type OverlayScrollbarsComponentRef,
} from 'overlayscrollbars-react';
import React, {
  type ComponentProps, type CSSProperties, forwardRef, type MouseEvent, useEffect, useMemo, useRef, useState,
} from 'react';
import { hexColorWithOpacityToString } from '~/_shared/components/colorPicker/colorPicker.helpers';
import type { Theme } from '~/_shared/themes/theme.model';
import { ScrollbarType } from './scrollbar.component';

const SCROLLBAR_WIDTH = 7;
const SCROLLBAR_ACTIVE_WIDTH = 11;
export const fadeHeightVariableName = '--fade-height';

type OverlayScrollbarProps = Readonly<{
  className?: string;
  isScrollbarHidden?: boolean;
  maxHeight?: number | string;
  type: ScrollbarType | ScrollbarType[];
  scrollbarStyle?: Interpolation;
  onClick?: (e: MouseEvent) => void;
  onUpdate?: EventListeners['scroll'];
  children?: React.ReactNode;
  style?: CSSProperties;
  showScrollFade?: boolean;
}>;

const baseTransition = 'visibility 0s 1s, opacity 1s linear, height .15s, width .15s';// fade out
const noVisibilityNorOpacityTransition = 'visibility 0s, opacity 0s, height .15s, width .15s'; // no fade in

const scrollbarVisibilityVariableName = '--os-scrollbar-visibility';

const scrollbarStyle = (theme: Theme) => css([
  {
    minWidth: '100%',
  },
  {
    '& .os-scrollbar': {
      '--os-handle-max-size': '150px',
      '--os-padding-axis': '3px',
      visibility: `var(${scrollbarVisibilityVariableName})` as CSSProperties['visibility'],
      zIndex: 999,
      transition: baseTransition, // fade out
      '& .os-scrollbar-handle': {
        transition: baseTransition, // fade out
      },
    },
    '& .os-scrollbar-vertical': {
      width: SCROLLBAR_WIDTH,
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'end',
      '&:hover, &:active': {
        width: SCROLLBAR_ACTIVE_WIDTH,
      },
      '& .os-scrollbar-handle': {
        width: SCROLLBAR_WIDTH,
        '&:hover, &:active': {
          width: SCROLLBAR_ACTIVE_WIDTH,
        },
      },
    },
    '& .os-scrollbar-horizontal': {
      height: SCROLLBAR_WIDTH,
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'end',
      '&:hover, &:active': {
        height: SCROLLBAR_ACTIVE_WIDTH,
      },
      '& .os-scrollbar-handle': {
        height: SCROLLBAR_WIDTH,
        '&:hover, &:active': {
          height: SCROLLBAR_ACTIVE_WIDTH,
        },
      },
    },
    '&:hover .os-scrollbar, .os-scrollbar:hover, .os-scrollbar:active': {
      transition: noVisibilityNorOpacityTransition, // fade in immediately
      opacity: 1,
    },
    '.os-scrollbar .os-scrollbar-handle': {
      opacity: 0.8,
      transition: noVisibilityNorOpacityTransition, // fade in immediately
      backgroundColor: theme.scrollbarColors.thumbColor,
      cursor: 'default',
      borderRadius: 5000, // Makes ends rounds.
    },
    '& .os-scrollbar:hover .os-scrollbar-handle': {
      opacity: 1,
      transition: noVisibilityNorOpacityTransition, // fade in immediately
    },
    '&:hover .os-scrollbar .os-scrollbar-handle.dragging, .os-scrollbar:hover .os-scrollbar-handle.dragging': {
      opacity: 0.35,
    },
    '[data-overlayscrollbars-viewport*="overflowYHidden"] ~ .os-scrollbar-vertical': {
      display: 'none',
    },
    '[data-overlayscrollbars-viewport*="overflowXHidden"] ~ .os-scrollbar-horizontal': {
      display: 'none',
    },
  }]);

const sentinelHeight = 10;

const hiddenStyles = css({
  display: 'none',
});

const topSentinelStyle = css({
  height: sentinelHeight,
  marginBottom: sentinelHeight * -1,
  pointerEvents: 'none',
});

const bottomSentinelStyle = css({
  height: sentinelHeight,
  marginTop: sentinelHeight * -1,
  pointerEvents: 'none',
});

const fadeCommon = css({
  height: 20,
  left: 0,
  opacity: 0,
  pointerEvents: 'none',
  position: 'absolute',
  right: 0,
  zIndex: 1,
  transition: 'opacity 0.3s ease',
});

const getGradient = (direction: 'to bottom' | 'to top', theme: Theme) => css({
  background: `linear-gradient(${direction}, ${hexColorWithOpacityToString(theme.backgroundColors.secondary, 0.8)}, transparent)`,
});

export const getFadeTopStyle = (theme: Theme) => css([
  fadeCommon,
  getGradient('to bottom', theme),
  { top: 0 },
]);

export const getFadeBottomStyle = (theme: Theme) => css([
  fadeCommon,
  getGradient('to top', theme),
  { bottom: 0 },
]);

const fadeStyles = (theme: Theme) => css({
  [fadeHeightVariableName]: '20%',

  '&:before': [
    getFadeTopStyle(theme),
    {
      content: '" "',
      height: 'var(--fade-height)',
    },
  ],

  '&:after': [
    getFadeBottomStyle(theme),
    {
      content: '" "',
      height: 'var(--fade-height)',
    },
  ],
});

const showFadeTopStyle = css({
  '&:before': {
    opacity: 1,
  },
});

const showFadeBottomStyle = css({
  '&:after': {
    opacity: 1,
  },
});

export const OverlayScrollbarComponent = forwardRef<OverlayScrollbarsComponentRef, OverlayScrollbarProps>(({
  className,
  isScrollbarHidden,
  maxHeight,
  type,
  scrollbarStyle: customScrollbarStyle,
  onClick,
  onUpdate,
  children,
  style,
  showScrollFade = false,
  ...restProps
}, scrollbarRef) => {
  const topSentinelRef = useRef<HTMLDivElement>(null);
  const bottomSentinelRef = useRef<HTMLDivElement>(null);
  const [showBottomFade, setShowBottomFade] = useState(false);
  const [showTopFade, setShowTopFade] = useState(false);
  const [removeSentinels, setRemoveSentinels] = useState(true);

  useEffect(() => {
    const topSentinelElement = topSentinelRef.current;
    const bottomSentinelElement = bottomSentinelRef.current;
    const parentElement = topSentinelElement?.parentElement;
    if (topSentinelElement && bottomSentinelElement && parentElement) {
      const scrollableContent = parentElement.querySelector('[data-element="scrollable-content"]');
      const options = {
        root: parentElement,
        threshold: 0.8,
      };

      const updateSentinels = () => {
        if (scrollableContent) {
          const { height } = scrollableContent.getBoundingClientRect();
          if (height <= sentinelHeight) {
            setRemoveSentinels(true);
            return;
          }
        }
        setRemoveSentinels(false);
      };
      const mutationObserver = new MutationObserver(updateSentinels);
      updateSentinels();

      mutationObserver.observe(parentElement, {
        childList: true,
        characterData: true,
        subtree: true,
      });

      const intersectionObserver = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.target === topSentinelElement) {
            setShowTopFade(!entry.isIntersecting);
          }

          if (entry.target === bottomSentinelElement) {
            setShowBottomFade(!entry.isIntersecting);
          }
        });
      }, options);

      intersectionObserver.observe(topSentinelElement);
      intersectionObserver.observe(bottomSentinelElement);

      return () => {
        intersectionObserver.disconnect();
        mutationObserver.disconnect();
      };
    }

    return undefined;
  }, []);

  const options: ComponentProps<typeof OverlayScrollbarsComponent>['options'] = useMemo(() => {
    const scrollbarTypes: ScrollbarType[] = Array.isArray(type) ? type : [type];
    return {
      scrollbars: {
        theme: 'os-theme-custom',
        autoHide: 'leave',
        autoHideDelay: 0,
        clickScroll: true,
        visibility: isScrollbarHidden ? 'hidden' : 'auto',
      },
      overflow: {
        x: scrollbarTypes.includes(ScrollbarType.Horizontal) ? 'scroll' : 'hidden',
        y: scrollbarTypes.includes(ScrollbarType.Vertical) ? 'scroll' : 'hidden',
      },
      update: {
        elementEvents: [['*', 'animationend'], ['*', 'transitionend']],
      },
    };
  }, [isScrollbarHidden, type]);

  const scrollbarVisibilityStyle = {
    [scrollbarVisibilityVariableName]: isScrollbarHidden ? 'hidden' : 'visible',
  };

  const events = useMemo(() => {
    const scroll: EventListeners['scroll'] = (instance, event) => {
      if (Array.isArray(onUpdate)) {
        onUpdate.forEach(fn => fn(instance, event));
      }
      else {
        onUpdate?.(instance, event);
      }
    };
    return {
      scroll,
    };
  }, [onUpdate]);

  return (
    <OverlayScrollbarsComponent
      {...restProps}
      ref={scrollbarRef}
      options={options}
      css={[
        customScrollbarStyle,
        scrollbarStyle,
        showScrollFade && fadeStyles,
        showTopFade && showFadeTopStyle,
        showBottomFade && showFadeBottomStyle,
      ]}
      style={{
        ...style,
        maxHeight,
        ...scrollbarVisibilityStyle,
      }}
      className={className}
      events={events}
      onClick={onClick}
    >
      <div
        className="top-sentinel"
        css={[topSentinelStyle, removeSentinels && hiddenStyles]}
        ref={topSentinelRef}
      />
      {children}
      <div
        className="bottom-sentinel"
        css={[bottomSentinelStyle, removeSentinels && hiddenStyles]}
        ref={bottomSentinelRef}
      />
    </OverlayScrollbarsComponent>
  );
});

OverlayScrollbarComponent.displayName = 'OverlayScrollbarComponent';
